/**
 * A tabela das sete etapas (E147.F3, ADR-0101): a etapa, a conferência da saída (o motivo de
 * aguardar, a contagem da aprovada, ou o critério que travou e a condição de saída, com a ação
 * principal) e a liberação, tudo lido do servidor. Os critérios abrem por expansão, uma etapa por
 * vez.
 */
import { Fragment, useState } from 'react';

import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type BadgeProps,
} from '@/components/ui';
import {
  acaoDaEtapa,
  liberacaoNaTela,
  type EtapaDoCasoData,
  type EtapaDoFluxo,
  type LiberacaoNaTela,
} from '@/lib/conferencia_das_etapas';

import { AcaoPrincipal } from './AcaoPrincipal';
import { CriteriosDaEtapa } from './CriteriosDaEtapa';
import { preencher, t, textoDaSaida } from './textos';

const VARIANTE: Record<LiberacaoNaTela, BadgeProps['variant']> = {
  aprovado: 'success',
  corrigir: 'warning',
  bloqueado: 'danger',
  aguardando: 'neutral',
};

const COLUNAS = 3;

function Linha({
  etapa,
  etapas,
  caseId,
  aberta,
  onAlternar,
}: {
  etapa: EtapaDoCasoData;
  etapas: EtapaDoCasoData[];
  caseId: string;
  aberta: boolean;
  onAlternar: () => void;
}): JSX.Element {
  const liberacao = liberacaoNaTela(etapa);
  const criterios = etapa.agora?.criterios.length ?? 0;
  return (
    <TableRow data-etapa={etapa.etapa} data-liberacao={liberacao}>
      <TableCell className="font-semibold text-fg">{t.etapa[etapa.etapa]}</TableCell>
      <TableCell className="space-y-sc-2 text-fg-muted">
        <p>{textoDaSaida(etapa)}</p>
        <div className="flex flex-wrap items-center gap-sc-2">
          <AcaoPrincipal acao={acaoDaEtapa(etapa, etapas)} caseId={caseId} />
          {criterios > 0 ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-expanded={aberta}
              onClick={onAlternar}
            >
              {aberta ? t.ocultarCriterios : preencher(t.verCriterios, { n: criterios })}
            </Button>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant={VARIANTE[liberacao]}>{t.liberacao[liberacao]}</Badge>
      </TableCell>
    </TableRow>
  );
}

export function TabelaDasEtapas({
  etapas,
  caseId,
}: {
  etapas: EtapaDoCasoData[];
  caseId: string;
}): JSX.Element {
  const [aberta, setAberta] = useState<EtapaDoFluxo | null>(null);
  return (
    <Table data-testid="conferencia-etapas">
      <TableHeader>
        <TableRow>
          <TableHead scope="col">{t.colunas.etapa}</TableHead>
          <TableHead scope="col">{t.colunas.conferencia}</TableHead>
          <TableHead scope="col">{t.colunas.liberacao}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {etapas.map((etapa) => (
          <Fragment key={etapa.etapa}>
            <Linha
              etapa={etapa}
              etapas={etapas}
              caseId={caseId}
              aberta={aberta === etapa.etapa}
              onAlternar={() => setAberta((atual) => (atual === etapa.etapa ? null : etapa.etapa))}
            />
            {aberta === etapa.etapa ? (
              <TableRow data-criterios-de={etapa.etapa}>
                <TableCell colSpan={COLUNAS}>
                  <CriteriosDaEtapa etapa={etapa} />
                </TableCell>
              </TableRow>
            ) : null}
          </Fragment>
        ))}
      </TableBody>
    </Table>
  );
}
